/* SANDY KISSI — Ritual Stage scroll system.
   Idempotent. One passive scroll listener, rAF-throttled, scenes only
   animate while intersecting. Never touches the product form, cart or
   sticky ATC. Theme-editor safe: section re-renders re-bind via
   shopify:section:load. Root classes:
     html.sandy-3d-anim → motion allowed (reveals, tilt, strip settle)
     html.sandy-3d-on   → scene live mode (wide viewport + motion)      */
(function () {
  'use strict';
  if (window.SandyKissi3D) return;
  window.SandyKissi3D = true;

  var root = document.documentElement;
  var motionMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var wideMq = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;
  var tallMq = window.matchMedia ? window.matchMedia('(min-height: 560px)') : null;
  var hoverMq = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : null;
  var hasIO = 'IntersectionObserver' in window;

  var motionOk = function () { return motionMq ? !motionMq.matches : false; };
  var sceneOk = function () { return motionOk() && wideMq && wideMq.matches && tallMq && tallMq.matches; };

  /* ---- Scenes: scroll progress → --sandy-3d-p + phase ----
     The written progress chases the scroll target through a small lerp,
     so flicks and trackpad jumps glide instead of snapping; phase flips
     carry hysteresis so a scroll parked on a boundary never flickers. */
  var LERP = 0.16;
  var SETTLED = 0.0008;
  var PHASE_HYST = 0.025;
  var scenes = [];
  var sceneIO = null;
  var raf = 0;

  var phaseFor = function (s, p) {
    var raw = Math.min(s.steps, 1 + Math.floor(p * s.steps));
    if (!s.phase) return raw;
    if (raw === s.phase) return s.phase;
    if (raw > s.phase) {
      return p >= (raw - 1) / s.steps + PHASE_HYST ? raw : s.phase;
    }
    return p <= (s.phase - 1) / s.steps - PHASE_HYST ? raw : s.phase;
  };

  var update = function () {
    raf = 0;
    var settling = false;
    for (var j = 0; j < scenes.length; j++) {
      var s = scenes[j];
      if (!s.active) continue;
      var rect = s.track.getBoundingClientRect();
      var span = rect.height - window.innerHeight;
      var target = span > 0 ? -rect.top / span : 0.5;
      target = target < 0 ? 0 : target > 1 ? 1 : target;
      var p = s.p == null ? target : s.p + (target - s.p) * LERP;
      if (Math.abs(target - p) < SETTLED) {
        p = target;
      } else {
        settling = true;
      }
      s.p = p;
      s.el.style.setProperty('--sandy-3d-p', p.toFixed(4));
      var phase = phaseFor(s, p);
      if (phase !== s.phase) {
        s.phase = phase;
        s.el.setAttribute('data-sandy-3d-phase', String(phase));
      }
    }
    if (settling) queue();
  };
  var queue = function () {
    if (!raf && root.classList.contains('sandy-3d-on')) {
      raf = window.requestAnimationFrame(update);
    }
  };

  var dropSceneIO = function () {
    if (sceneIO) { sceneIO.disconnect(); sceneIO = null; }
  };
  var buildScenes = function () {
    dropSceneIO();
    scenes = [];
    var els = document.querySelectorAll('[data-sandy-3d-scene]');
    for (var i = 0; i < els.length; i++) {
      scenes.push({
        el: els[i],
        track: els[i].querySelector('[data-sandy-3d-track]') || els[i],
        steps: els[i].querySelectorAll('[data-sandy-3d-step]').length || 3,
        active: false,
        p: null,
        phase: 0
      });
    }
  };
  var enableScenes = function () {
    root.classList.add('sandy-3d-on');
    for (var j = 0; j < scenes.length; j++) scenes[j].el.classList.add('is-live');
    if (hasIO) {
      if (!sceneIO) {
        sceneIO = new IntersectionObserver(function (entries) {
          for (var k = 0; k < entries.length; k++) {
            for (var j2 = 0; j2 < scenes.length; j2++) {
              if (scenes[j2].track === entries[k].target) scenes[j2].active = entries[k].isIntersecting;
            }
          }
          queue();
        });
        for (var j3 = 0; j3 < scenes.length; j3++) sceneIO.observe(scenes[j3].track);
      }
    } else {
      for (var j4 = 0; j4 < scenes.length; j4++) scenes[j4].active = true;
    }
    queue();
  };
  var disableScenes = function () {
    root.classList.remove('sandy-3d-on');
    for (var j = 0; j < scenes.length; j++) {
      var s = scenes[j];
      s.el.classList.remove('is-live');
      s.el.style.removeProperty('--sandy-3d-p');
      s.el.removeAttribute('data-sandy-3d-phase');
      s.active = false;
      s.p = null;
      s.phase = 0;
    }
    dropSceneIO();
  };

  /* ---- One-time reveals + strip settle (re-observable) ---- */
  var revealIO = null;
  var observeReveals = function () {
    if (!hasIO) return;
    if (!revealIO) {
      revealIO = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (entries[k].isIntersecting) {
            entries[k].target.classList.add('is-in');
            revealIO.unobserve(entries[k].target);
          }
        }
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });
    }
    var targets = document.querySelectorAll('[data-sandy-3d-reveal]:not(.is-in), [data-sandy-3d-strip]:not(.is-in)');
    for (var t = 0; t < targets.length; t++) revealIO.observe(targets[t]);
  };

  /* ---- Pointer tilt (desktop hover only, bound per element) ---- */
  var bindTilt = function () {
    if (!hoverMq || !hoverMq.matches) return;
    var els = document.querySelectorAll('[data-sandy-3d-tilt]');
    for (var t = 0; t < els.length; t++) {
      if (els[t].getAttribute('data-sandy-3d-tilt-bound')) continue;
      els[t].setAttribute('data-sandy-3d-tilt-bound', '1');
      (function (el) {
        var tRaf = 0, tx = 0, ty = 0;
        var apply = function () {
          tRaf = 0;
          el.style.setProperty('--sandy-3d-tx', tx.toFixed(3));
          el.style.setProperty('--sandy-3d-ty', ty.toFixed(3));
        };
        el.addEventListener('pointermove', function (e) {
          if (!root.classList.contains('sandy-3d-anim')) return;
          var r = el.getBoundingClientRect();
          tx = ((e.clientX - r.left) / r.width) * 2 - 1;
          ty = ((e.clientY - r.top) / r.height) * 2 - 1;
          if (!tRaf) tRaf = window.requestAnimationFrame(apply);
        }, { passive: true });
        el.addEventListener('pointerleave', function () {
          tx = 0; ty = 0;
          if (!tRaf) tRaf = window.requestAnimationFrame(apply);
        }, { passive: true });
      })(els[t]);
    }
  };

  /* ---- Mode sync ---- */
  var sync = function () {
    if (motionOk() && hasIO) {
      root.classList.add('sandy-3d-anim');
      observeReveals();
      bindTilt();
    } else {
      root.classList.remove('sandy-3d-anim');
    }
    if (sceneOk()) enableScenes(); else disableScenes();
  };

  var onMq = function () { sync(); };
  var listen = function (mq) {
    if (!mq) return;
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);
  };
  listen(motionMq); listen(wideMq); listen(tallMq); listen(hoverMq);

  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue, { passive: true });

  /* Theme editor: a section re-render swaps DOM nodes. Fail the fresh
     reveal targets open immediately, then rebuild scene/tilt bindings. */
  document.addEventListener('shopify:section:load', function (e) {
    var scope = e.target || document;
    var els = scope.querySelectorAll('[data-sandy-3d-reveal], [data-sandy-3d-strip]');
    for (var i = 0; i < els.length; i++) els[i].classList.add('is-in');
    buildScenes();
    sync();
  });

  var start = function () { buildScenes(); sync(); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
